import React, { useCallback, useState } from 'react';
import Field from '../components/Field';
import StatusBadge from '../components/StatusBadge';
import {
  CALC_METHODS,
  gatherInputs,
  computeRights,
  inputLabel,
  fmt,
} from '../engine/rights-engine';
import { computeLayers, isVestedLayerRole } from '../engine/layers';
import { field, planRoleLabel, STATUS } from '../state/model';

// זכויות — the deterministic rights surface. Left: editable rights inputs
// (seeded from the main-rights plan, awaiting approval). Right/below: computed
// metrics, each with an expandable, reproducible explanation. Missing inputs
// render as tasks, never as silent zeros.
export default function RightsTab({ project, updateProject }) {
  const setRight = useCallback((key, partial) => {
    updateProject((p) => { p.rights[key] = { ...p.rights[key], ...partial }; }, `עודכן נתון זכויות: ${key}`);
  }, [updateProject]);

  const setParcelArea = useCallback((partial) => {
    updateProject((p) => { p.property.parcelArea = { ...p.property.parcelArea, ...partial }; }, 'עודכן שטח מגרש');
  }, [updateProject]);

  const setLayerAddition = useCallback((itemId, value) => {
    updateProject((p) => {
      const it = p.planStack.find((x) => x.id === itemId);
      if (!it) return;
      const empty = value === '' || value == null;
      it.rightsAddition = {
        ...(it.rightsAddition || field()),
        value: empty ? null : Number(value),
        status: empty ? STATUS.MISSING : STATUS.MANUAL,
      };
    }, 'עודכנה תוספת זכויות לשכבה');
  }, [updateProject]);

  const inputs = gatherInputs(project);
  const metrics = computeRights(inputs);
  const sourceRef = project.rights.sourceRef;
  const get = (k) => metrics.find((m) => m.key === k);
  const remArea = get('remainingArea');
  const allowedTotal = get('allowedTotal');
  const existingArea = get('existingArea');
  const allowedCoverage = get('allowedCoverage');
  const allowedUnits = get('allowedUnits');
  const maxStories = get('maxStories');
  const maxHeight = get('maxHeight');
  const parking = get('parkingRequired');
  const overBuilt = remArea?.status === 'ok' && remArea.value < 0;
  const layered = computeLayers(project, metrics);
  const [openMetric, setOpenMetric] = useState('remainingArea');

  const methodLabel = (CALC_METHODS.find((m) => m.id === inputs.calcMethod) || {}).label || '';

  return (
    <div className="rd-rights-tab">
      {/* ---- Equation hero: allowed − existing = remaining ----------------- */}
      <section className="rd-card" style={{ padding: '26px 28px' }}>
        <h3 className="rd-card-title" style={{ marginBottom: 18 }}>יתרת זכויות — תמצית</h3>
        <div className="rd-equation">
          <div className="rd-eq-card allowed">
            <div className="rd-eq-label">מותר לפי תכנית הבסיס</div>
            <div className="rd-eq-value">
              {allowedTotal.status === 'ok' ? <>{fmt(allowedTotal.value)} <span className="unit">מ"ר</span></> : '—'}
            </div>
            <div className="rd-eq-caption">
              {allowedTotal.status === 'ok'
                ? `${sourceRef ? `תכנית ${sourceRef} · ` : ''}${methodLabel}`
                : `חסר: ${allowedTotal.missingInputs.map(inputLabel).join(', ')}`}
            </div>
          </div>
          <div className="rd-eq-op">−</div>
          <div className="rd-eq-card existing">
            <div className="rd-eq-label">בנוי קיים</div>
            <div className="rd-eq-value">
              {existingArea.status === 'ok' ? <>{fmt(existingArea.value)} <span className="unit">מ"ר</span></> : '—'}
            </div>
            <div className="rd-eq-caption">מתוך סיכום מצב קיים</div>
          </div>
          <div className="rd-eq-op">=</div>
          <div className={`rd-eq-card ${remArea.status === 'ok' && remArea.value < 0 ? 'result-neg' : 'result-pos'}`}>
            <div className="rd-eq-label">יתרת זכויות (שטח)</div>
            <div className="rd-eq-value">
              {remArea.status === 'ok' ? <><span dir="ltr">{fmt(remArea.value)}</span> <span className="unit">מ"ר</span></> : '—'}
            </div>
            <div className="rd-eq-caption">מול תכנית הבסיס בלבד</div>
          </div>
        </div>
        <div className="rd-eq-chips">
          {allowedCoverage.status === 'ok' && <span className="rd-eq-chip">תכסית מותרת · {fmt(allowedCoverage.value)} מ"ר</span>}
          {maxStories.status === 'ok' && <span className="rd-eq-chip">קומות מותר · {fmt(maxStories.value)}</span>}
          {allowedUnits.status === 'missing' && <span className="rd-eq-chip miss">יח"ד · חסר נתון צפיפות</span>}
          {maxHeight.status === 'missing' && <span className="rd-eq-chip miss">גובה · חסר</span>}
          {parking.status === 'missing' && <span className="rd-eq-chip miss">חניות · חסר תקן</span>}
        </div>
        {overBuilt && (
          <div className="rd-overbuilt-note">
            המבנה הקיים גדול מהמותר לפי תכנית הבסיס — הפער מומש ככל הנראה מכוח תכניות מאוחרות (למשל תמ"א 38).
            חישוב יתרה מלא דורש את מימוש שכבות ה-Plan Stack — ראו "שכבות זכויות" בהמשך המסך.
          </div>
        )}
      </section>

      {/* ---- Rights inputs ------------------------------------------------- */}
      <section className="rd-card" data-rd-anchor="card-rightsinputs">
        <h3 className="rd-card-title">נתוני זכויות (קלט למנוע)</h3>
        <p className="rd-card-sub">
          {sourceRef
            ? `מקור הזכויות הראשי: תכנית ${sourceRef}. נתונים שחולצו ממנה מסומנים "חולץ אוטומטית"; את החסר יש להשלים ידנית מתוך התכנית.`
            : 'לא הוגדר מקור זכויות ראשי. הזן נתונים ידנית או סווג תכנית כ"מקור זכויות ראשי" בלשונית פרטי הנכס.'}
        </p>
        <div className="rd-field-group">
          <div className="rd-field-group-title">שטח ובנייה</div>
          <div className="rd-field-grid">
            <Field label='שטח מגרש (מ"ר)' field={project.property.parcelArea} type="number" onChange={setParcelArea}
              hint="קלט מרכזי — נדרש כמעט לכל חישוב. נלקח מנתוני הנכס." />
            <Field label="אחוזי בנייה (%)" field={project.rights.farPct} anchor="field-farPct" type="number" onChange={(p) => setRight('farPct', p)} />
            <Field label="תכסית מותרת (%)" field={project.rights.coveragePct} type="number" onChange={(p) => setRight('coveragePct', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">גובה וקומות</div>
          <div className="rd-field-grid">
            <Field label="קומות מותר" field={project.rights.maxStories} anchor="field-maxStories" type="number" onChange={(p) => setRight('maxStories', p)} />
            <Field label="גובה מותר (מ')" field={project.rights.maxHeight} anchor="field-maxHeight" type="number" onChange={(p) => setRight('maxHeight', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">צפיפות וחניה</div>
          <div className="rd-field-grid">
            <Field label='צפיפות (יח"ד/דונם)' field={project.rights.densityPerDunam} type="number" onChange={(p) => setRight('densityPerDunam', p)} />
            <Field label='חניות ליח"ד' field={project.rights.parkingRatio} type="number" onChange={(p) => setRight('parkingRatio', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">שיטת חישוב</div>
          <div className="rd-field-grid">
            <Field label="שיטת חישוב שטחים" field={project.rights.calcMethod} type="select" options={CALC_METHODS} onChange={(p) => setRight('calcMethod', p)} />
          </div>
        </div>
      </section>

      {/* ---- Computed metrics --------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">תוצאות חישוב</h3>
        <p className="rd-card-sub">לחצו על שורה לנוסחה המלאה — קלטים, הנחות ומקור.</p>
        <div className="rd-metrics">
          {metrics.map((m) => (
            <MetricRow
              key={m.key}
              m={m}
              open={openMetric === m.key}
              onToggle={() => setOpenMetric(openMetric === m.key ? null : m.key)}
            />
          ))}
        </div>
      </section>

      {/* ---- Multi-layer envelope ------------------------------------------ */}
      <section className="rd-card" data-rd-anchor="card-layers">
        <h3 className="rd-card-title">מעטפת מלאה — שכבות זכויות</h3>
        <p className="rd-card-sub">
          השכבות נגזרות מתפקידי ה-Plan Stack; את התוספת הכמותית של כל שכבה (למשל זכויות תמ"א 38) מזין איש המקצוע —
          המערכת אינה אומדת אותה אוטומטית. תוספות של תכניות מסוג Modifier נספרות במעטפת המוקנית; פוטנציאל נספר בנפרד ואינו זכות מוקנית.
        </p>
        {layered.layers.length === 0 ? (
          <div className="rd-empty">אין שכבות רלוונטיות ב-Plan Stack (מקור ראשי / Modifier / פוטנציאל).</div>
        ) : (
          <>
            <table className="rd-table">
              <thead>
                <tr><th>שכבה</th><th>תפקיד</th><th>תוספת שטח (מ"ר)</th><th>נספרת</th></tr>
              </thead>
              <tbody>
                {layered.layers.map((it) => {
                  const isMain = it.role.value === 'main_rights';
                  const vested = isVestedLayerRole(it.role.value);
                  return (
                    <tr key={it.id}>
                      <td>
                        <div className="rd-plan-title">{it.title.value || it.planRef}</div>
                        {it.planUrl && <a href={it.planUrl} target="_blank" rel="noreferrer" className="rd-timeline-link">מסמכי התכנית ↗</a>}
                      </td>
                      <td>{planRoleLabel(it.role.value)}</td>
                      <td>
                        {isMain ? (
                          <span className="rd-muted" title="שכבת הבסיס מחושבת מנתוני הזכויות למעלה">
                            בסיס: {layered.base != null ? `${fmt(layered.base)} מ"ר` : 'לא חושב'}
                          </span>
                        ) : (
                          <input
                            className="rd-input rd-input-sm rd-layer-input"
                            type="number"
                            placeholder="—"
                            value={it.rightsAddition?.value ?? ''}
                            onChange={(e) => setLayerAddition(it.id, e.target.value)}
                          />
                        )}
                      </td>
                      <td>
                        {isMain
                          ? <StatusBadge status={STATUS.APPROVED} title="שכבת הבסיס" />
                          : vested
                            ? <span className="rd-fits">במעטפת המוקנית</span>
                            : <span className="rd-metric-missing">פוטנציאל בלבד</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="rd-layers-summary">
              <span>בסיס: <b>{layered.base != null ? fmt(layered.base) : '—'}</b> מ"ר</span>
              <span>+ תוספות מוקנות: <b>{fmt(layered.vestedAdd)}</b> מ"ר</span>
              <span>= מעטפת מוקנית: <b>{layered.vestedFull != null ? fmt(layered.vestedFull) : '—'}</b> מ"ר</span>
              {layered.remainingFull != null && (
                <span>
                  יתרה מול המעטפת המלאה:{' '}
                  <b className={layered.remainingFull < 0 ? 'rd-neg' : 'rd-layers-pos'}>{fmt(layered.remainingFull)} מ"ר</b>
                </span>
              )}
              {layered.potentialAdd > 0 && (
                <span>פוטנציאל נוסף (לא מוקנה): <b>{fmt(layered.potentialAdd)}</b> מ"ר</span>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function MetricRow({ m, open, onToggle }) {
  const isMissing = m.status === 'missing';
  return (
    <div className={`rd-metric ${isMissing ? 'missing' : ''}`}>
      <div className="rd-metric-head" onClick={onToggle}>
        <span className="rd-metric-chev">{open ? '▾' : '◂'}</span>
        <span className="rd-metric-label">{m.label}</span>
        <span className={`rd-metric-value ${!isMissing && m.value < 0 ? 'rd-neg' : ''}`}>
          {isMissing
            ? <span className="rd-metric-missing">חסר: {m.missingInputs.map(inputLabel).join(', ')}</span>
            : <>{fmt(m.value, m.decimals)} {m.unit}</>}
        </span>
      </div>
      {open && (
        <div className="rd-metric-detail">
          {m.formula && <div className="rd-metric-formula">{m.formula}</div>}
          {isMissing && (
            <div className="rd-metric-task">להשלמת החישוב נדרש: {m.missingInputs.map(inputLabel).join(', ')}</div>
          )}
          {m.assumptions?.length > 0 && (
            <ul className="rd-metric-assumptions">
              {m.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
